import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DesktopBootstrap } from '../../../shared-types/src/index.js';
import { describeError } from '../errors.js';
import { createAdminHttp } from '../http.js';
import type { AdminSession } from '../types.js';
import { boundedSessionRevoke, negotiateSessionProtocol, validateSessionPair } from '../../../shared-types/src/session-client.js';

const LOCAL_DEV_ADMIN = {
  tenantId: 'tnt_veluga_dev',
  email: 'test001@veluga.io'
} as const;

export function LoginScreen({ baseUrl, onLoggedIn, beginLogin, notice }: { baseUrl: string; onLoggedIn: (session: AdminSession, generation: number) => void; beginLogin: () => number; notice: string | undefined }) {
  const { t } = useTranslation();
  const [tenantId, setTenantId] = useState<string>(LOCAL_DEV_ADMIN.tenantId);
  const [email, setEmail] = useState<string>(LOCAL_DEV_ADMIN.email);
  const [password, setPassword] = useState<string>('');
  const [mode, setMode] = useState<'tenant' | 'platform' | 'partner'>('tenant');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const attempt = useRef(0);
  useEffect(() => () => { attempt.current++; }, [baseUrl]);
  const ready =
    mode === 'tenant'
      ? Boolean(tenantId.trim() && email.trim() && password)
      : Boolean(email.trim() && password);

  const client = useMemo(() => {
    let accessToken: string | undefined;
    return {
      setAccessToken: (token: string) => {
        accessToken = token;
      },
      http: createAdminHttp({ baseUrl, getAccessToken: () => accessToken })
    };
  }, [baseUrl]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready || busy) return;
    const request = ++attempt.current;
    const generation = beginLogin();
    setBusy(true);
    setError('');
    let issued: AdminSession | undefined;
    try {
      const sessionProtocol = await negotiateSessionProtocol(() => client.http.sessionCapabilities());
      if (request !== attempt.current) return;
      if (mode === 'platform') {
        const tokens = await client.http.platformLoginWithPassword(email.trim(), password, sessionProtocol);
        issued = { ...tokens, bootstrap: platformBootstrap(email.trim()) };
      } else if (mode === 'partner') {
        const result = await client.http.partnerLoginWithPassword(email.trim(), password, sessionProtocol);
        issued = {
          ...result,
          bootstrap: partnerBootstrap(email.trim())
        };
      } else {
        const tokens = await client.http.loginWithPassword(email.trim(), password, tenantId.trim(), sessionProtocol);
        issued = { ...tokens, bootstrap: platformBootstrap(email.trim()) };
        validateSessionPair(tokens, sessionProtocol);
        if (request !== attempt.current) throw new Error('session_cancelled');
        client.setAccessToken(tokens.accessToken);
        const bootstrap = await client.http.bootstrap();
        issued = { ...tokens, bootstrap };
      }
      validateSessionPair(issued, sessionProtocol);
      if (request !== attempt.current) throw new Error('session_cancelled');
      onLoggedIn(issued, generation);
    } catch (err) {
      if (issued?.sessionProtocol === 'rotating_v1') void boundedSessionRevoke(() => client.http.revokeSession({ refreshToken: issued!.refreshToken }));
      if (request === attempt.current) setError(describeError(err, t, 'errors.loginFailed'));
    } finally {
      client.setAccessToken('');
      if (request === attempt.current) setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-panel">
        <div>
          <div className="brand">Voost AI</div>
          <h1>{t('login.adminConsole')}</h1>
          <p>
            {mode === 'platform'
              ? t('login.platformHelp')
              : mode === 'partner'
                ? t('login.partnerHelp')
                : t('login.tenantHelp')}
          </p>
        </div>
        <form onSubmit={submit} className="form-grid">
          <label>
            {t('login.mode')}
            <select value={mode} onChange={(event) => setMode(event.target.value as 'tenant' | 'platform' | 'partner')}>
              <option value="tenant">{t('login.tenantAdmin')}</option>
              <option value="partner">{t('login.partnerAdmin')}</option>
              <option value="platform">{t('login.platformAdmin')}</option>
            </select>
          </label>
          {mode !== 'tenant' ? (
            <>
              <label>
                {t('login.email')}
                <input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} />
              </label>
              <label>
                {t('login.password')}
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </label>
            </>
          ) : (
            <>
              <label>
                {t('login.tenantId')}
                <input value={tenantId} onChange={(event) => setTenantId(event.target.value)} />
              </label>
              <label>
                {t('login.email')}
                <input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} />
              </label>
              <label>
                {t('login.password')}
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </label>
            </>
          )}
          {notice ? <p role="status">{t(`errors.codes.${notice}`)}</p> : null}
          <p className="muted">{t('login.sessionTabHelp')}</p>
          {error ? <div className="error">{error}</div> : null}
          <button disabled={!ready || busy}>{busy ? t('login.signingIn') : t('login.signIn')}</button>
        </form>
      </section>
    </main>
  );
}

function partnerBootstrap(email: string): DesktopBootstrap {
  return {
    user: { id: email, email, name: email, status: 'active' },
    tenant: { id: '__platform__', name: 'Partner Console', slug: 'partner', deploymentProfile: 'hosted', status: 'active' },
    membership: { tenantId: '__platform__', userId: email, roles: ['partner_admin'], groupIds: [] },
    entitlement: {
      tenantId: '__platform__',
      userId: email,
      seatStatus: 'assigned',
      desktopAccess: false,
      allowedModelIds: [],
      allowedFeatures: ['partner-console'],
      downloadChannels: [],
      allowedPlatforms: []
    },
    accountFlags: { mustChangePassword: false },
    availableModels: [],
    defaultModelId: '',
    selectedModelId: '',
    effectivePolicy: {} as DesktopBootstrap['effectivePolicy'],
    gateway: { baseUrl: '', token: '', expiresAt: '', compatibility: { chatCompletions: false, responses: false, claudeMessages: false, velugaAgentRuns: false } }
  };
}

function platformBootstrap(email: string): DesktopBootstrap {
  return {
    user: { id: email, email, name: email, status: 'active' },
    tenant: { id: '__platform__', name: 'Voost AI Platform', slug: 'platform', deploymentProfile: 'hosted', status: 'active' },
    membership: { tenantId: '__platform__', userId: email, roles: ['platform_admin'], groupIds: [] },
    entitlement: {
      tenantId: '__platform__',
      userId: email,
      seatStatus: 'assigned',
      desktopAccess: false,
      allowedModelIds: [],
      allowedFeatures: ['platform-console'],
      downloadChannels: [],
      allowedPlatforms: []
    },
    accountFlags: { mustChangePassword: false },
    availableModels: [],
    defaultModelId: '',
    selectedModelId: '',
    effectivePolicy: {} as DesktopBootstrap['effectivePolicy'],
    gateway: { baseUrl: '', token: '', expiresAt: '', compatibility: { chatCompletions: false, responses: false, claudeMessages: false, velugaAgentRuns: false } }
  };
}
